import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Mountain, Users, Calendar, FolderOpen, 
  BookOpen, Images, Bed, MessageSquare, Heart, Settings,
  LogOut, Menu, X, ChevronRight, Plus, Upload, Search
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIDEBAR_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: Mountain, label: 'Falesie', path: '/admin/crags' },
  { icon: Users, label: 'Team', path: '/admin/team' },
  { icon: Calendar, label: 'Eventi', path: '/admin/events' },
  { icon: FolderOpen, label: 'Progetti', path: '/admin/projects' },
  { icon: BookOpen, label: 'Blog', path: '/admin/blog' },
  { icon: Images, label: 'Gallery', path: '/admin/gallery' },
  { icon: Bed, label: 'Alloggi', path: '/admin/accommodations' },
  { icon: MessageSquare, label: 'Messaggi', path: '/admin/messages' },
  { icon: Heart, label: 'Donazioni', path: '/admin/donations' },
  { icon: Search, label: 'SEO', path: '/admin/seo' },
  { icon: Upload, label: 'Uploads', path: '/admin/uploads' },
  { icon: Settings, label: 'Impostazioni', path: '/admin/settings' },
];

export function AdminDashboard() {
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({
    crags: 0,
    routes: 0,
    events: 0,
    messages: 0,
    donations: 0
  });
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }

    const fetchData = async () => {
      try {
        const [cragsRes, routesRes, eventsRes, messagesRes] = await Promise.all([
          axios.get(`${API}/crags`),
          axios.get(`${API}/routes`),
          axios.get(`${API}/events`),
          axios.get(`${API}/contact/messages?unread_only=true`)
        ]);

        setStats({
          crags: cragsRes.data.length,
          routes: routesRes.data.length,
          events: eventsRes.data.length,
          messages: messagesRes.data.length,
          donations: 0
        });
        setRecentMessages(messagesRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, authLoading, navigate]);

  const initData = async () => {
    try {
      await axios.post(`${API}/init-data`);
      toast.success('Dati inizializzati con successo!');
      window.location.reload();
    } catch (error) {
      toast.error('Errore nell\'inizializzazione');
    }
  };

  if (authLoading || !isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0A0A0A] transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-800">
          <Link to="/" className="text-white font-heading text-lg uppercase">
            CMS Admin
          </Link>
          <button className="lg:hidden text-white" onClick={() => setSidebarOpen(false)}>
            <X className="w-6 h-6" />
          </button>
        </div>

        <nav className="p-4 space-y-1">
          {SIDEBAR_ITEMS.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
            >
              <item.icon className="w-5 h-5" />
              <span className="text-sm">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800">
          <button
            onClick={logout}
            className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white w-full rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="bg-white border-b h-16 flex items-center justify-between px-6">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-4 ml-auto">
            <span className="text-sm text-gray-600">
              {language === 'it' ? 'Benvenuto' : 'Welcome'}, <strong>{user?.name}</strong>
            </span>
            <Link to="/" className="text-sm text-[#FF6D00] hover:underline">
              {language === 'it' ? 'Vedi sito' : 'View site'}
            </Link>
          </div>
        </header>

        {/* Dashboard Content */}
        <main className="p-6">
          <div className="flex justify-between items-center mb-8">
            <h1 className="font-heading text-3xl uppercase">Dashboard</h1>
            <Button onClick={initData} variant="outline" className="text-sm">
              <Plus className="w-4 h-4 mr-2" />
              Inizializza dati default
            </Button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg border">
              <div className="flex items-center justify-between mb-4">
                <Mountain className="w-8 h-8 text-[#FF6D00]" />
                <span className="text-3xl font-heading font-bold">{stats.crags}</span>
              </div>
              <p className="text-gray-600 text-sm">Falesie</p>
            </div>
            <div className="bg-white p-6 rounded-lg border">
              <div className="flex items-center justify-between mb-4">
                <ChevronRight className="w-8 h-8 text-[#FF6D00]" />
                <span className="text-3xl font-heading font-bold">{stats.routes}</span>
              </div>
              <p className="text-gray-600 text-sm">Vie</p>
            </div>
            <div className="bg-white p-6 rounded-lg border">
              <div className="flex items-center justify-between mb-4">
                <Calendar className="w-8 h-8 text-[#FF6D00]" />
                <span className="text-3xl font-heading font-bold">{stats.events}</span>
              </div>
              <p className="text-gray-600 text-sm">Eventi</p>
            </div>
            <div className="bg-white p-6 rounded-lg border">
              <div className="flex items-center justify-between mb-4">
                <MessageSquare className="w-8 h-8 text-[#FF6D00]" />
                <span className="text-3xl font-heading font-bold">{stats.messages}</span>
              </div>
              <p className="text-gray-600 text-sm">Messaggi non letti</p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg border">
              <h2 className="font-heading text-xl uppercase mb-4">Azioni rapide</h2>
              <div className="grid grid-cols-2 gap-4">
                <Link to="/admin/crags" className="flex items-center gap-2 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <Mountain className="w-5 h-5 text-[#FF6D00]" />
                  <span className="text-sm">Gestisci falesie</span>
                </Link>
                <Link to="/admin/events" className="flex items-center gap-2 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <Calendar className="w-5 h-5 text-[#FF6D00]" />
                  <span className="text-sm">Aggiungi evento</span>
                </Link>
                <Link to="/admin/blog" className="flex items-center gap-2 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <BookOpen className="w-5 h-5 text-[#FF6D00]" />
                  <span className="text-sm">Scrivi articolo</span>
                </Link>
                <Link to="/admin/gallery" className="flex items-center gap-2 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <Upload className="w-5 h-5 text-[#FF6D00]" />
                  <span className="text-sm">Carica immagini</span>
                </Link>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg border">
              <h2 className="font-heading text-xl uppercase mb-4">Messaggi recenti</h2>
              {recentMessages.length > 0 ? (
                <ul className="space-y-3">
                  {recentMessages.map((msg) => (
                    <li key={msg.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <MessageSquare className="w-5 h-5 text-[#FF6D00] flex-shrink-0 mt-0.5" />
                      <div className="flex-grow min-w-0">
                        <p className="font-medium text-sm truncate">{msg.name}</p>
                        <p className="text-gray-500 text-xs truncate">{msg.message}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 text-sm">Nessun messaggio nuovo</p>
              )}
              <Link to="/admin/messages" className="inline-flex items-center text-[#FF6D00] text-sm mt-4 hover:underline">
                Vedi tutti i messaggi
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </div>

          {/* Help Section */}
          <div className="bg-[#FF6D00]/5 border border-[#FF6D00]/20 p-6 rounded-lg">
            <h2 className="font-heading text-xl uppercase mb-2">Guida rapida</h2>
            <p className="text-gray-600 text-sm mb-4">
              Usa il menu laterale per navigare tra le sezioni del CMS. Puoi gestire contenuti, 
              falesie, eventi, blog e molto altro. Tutti i contenuti supportano traduzioni multiple.
            </p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Per importare vie da CSV, vai in Falesie &gt; Seleziona falesia &gt; Import CSV</li>
              <li>• Per tradurre i contenuti, modifica ogni campo nella lingua desiderata</li>
              <li>• Le modifiche sono salvate automaticamente</li>
            </ul>
          </div>
        </main>
      </div>
    </div>
  );
}
